// ── CONFIRMATION MODAL ────────────────────────────────────────────────────────
// Reusable modal for high-consequence actions — those that fire QB pushes,
// lock records, commit money, or have cascading side effects. Names the user,
// names the action, names the consequence, asks for explicit confirm. Reserved
// for moments where the consequence isn't obvious from the button label;
// routine actions don't get modals.
//
// Props:
//   open          — boolean. When false, returns null (modal is unmounted-equivalent).
//   heading       — string. Top line of the card.
//   body          — string | node. Multi-line copy. Strings render as <div>; nodes pass through.
//   children      — optional. Extra form controls inside the modal (e.g., a radio group
//                   for the three-option Edit modal in ProposalsTab2). Renders between
//                   body and the button row.
//   confirmLabel  — string. Default 'Confirm'.
//   cancelLabel   — string. Default 'Cancel'.
//   onConfirm     — function. Called when user clicks Confirm.
//   onCancel      — function. Called when user clicks Cancel, clicks the backdrop, or hits Esc.
//   confirming    — boolean. When true, disables both buttons and shows '…' on confirm.
//   tone          — 'primary' | 'danger' | 'warning'. Confirm-button color. Default 'primary'.
//   error         — optional string. Renders red below the button row when set.
//
// Style cues from existing inline modals (DesignerHoursSection.jsx:973,
// PaymentsPanel, etc.): fixed full-screen backdrop rgba(0,0,0,0.55), centered
// white card, hairline borders.

import React, { useEffect } from 'react';
var h = React.createElement;

const TONE_BG = {
  primary: '#1a1a1a',
  danger:  '#C8322A',
  warning: '#D4007A',
};

const STYLES = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    zIndex: 1200,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  card: {
    width: '100%', maxWidth: 480,
    background: '#fff',
    borderRadius: 6,
    boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
    padding: 22,
    fontFamily: "'DM Sans', sans-serif",
  },
  heading: {
    fontSize: 14, fontWeight: 700, color: '#111',
    marginBottom: 10,
  },
  body: {
    fontSize: 13, color: '#444', lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    marginBottom: 14,
  },
  buttonRow: {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    marginTop: 6,
  },
  cancelBtn: {
    padding: '7px 14px',
    background: '#fff',
    border: '0.5px solid #D8D7D2',
    borderRadius: 5,
    fontSize: 12, fontWeight: 500,
    fontFamily: 'inherit',
    color: '#111',
    cursor: 'pointer',
  },
  confirmBtn: {
    padding: '7px 14px',
    color: '#fff',
    border: 'none',
    borderRadius: 5,
    fontSize: 12, fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  error: {
    marginTop: 12,
    fontSize: 12,
    color: '#C8322A',
  },
};

export default function ConfirmationModal(p) {
  // ESC closes the modal (unless mid-confirm).
  useEffect(function() {
    if (!p.open) return;
    function onKey(e) {
      if (e.key === 'Escape' && !p.confirming && p.onCancel) p.onCancel();
    }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [p.open, p.confirming, p.onCancel]);

  if (!p.open) return null;

  const tone = p.tone || 'primary';
  const confirmBg = TONE_BG[tone] || TONE_BG.primary;
  const confirming = !!p.confirming;
  const confirmLabel = p.confirmLabel || 'Confirm';
  const cancelLabel = p.cancelLabel || 'Cancel';

  return h('div', {
    style: STYLES.backdrop,
    'data-testid': 'confirmation-modal-backdrop',
    onClick: function(e) {
      if (e.target === e.currentTarget && !confirming && p.onCancel) p.onCancel();
    },
  },
    h('div', { style: STYLES.card, role: 'dialog', 'aria-modal': 'true', 'data-testid': 'confirmation-modal' },
      p.heading && h('div', { style: STYLES.heading, 'data-testid': 'confirmation-modal-heading' }, p.heading),
      p.body && (typeof p.body === 'string'
        ? h('div', { style: STYLES.body, 'data-testid': 'confirmation-modal-body' }, p.body)
        : h('div', { style: STYLES.body, 'data-testid': 'confirmation-modal-body' }, p.body)
      ),
      p.children && h('div', { style: { marginBottom: 14 } }, p.children),
      h('div', { style: STYLES.buttonRow },
        h('button', {
          type: 'button',
          onClick: p.onCancel,
          disabled: confirming,
          style: Object.assign({}, STYLES.cancelBtn, confirming ? { opacity: 0.5, cursor: 'default' } : {}),
          'data-testid': 'confirmation-modal-cancel',
        }, cancelLabel),
        h('button', {
          type: 'button',
          onClick: p.onConfirm,
          disabled: confirming,
          style: Object.assign({}, STYLES.confirmBtn, { background: confirmBg }, confirming ? { opacity: 0.6, cursor: 'wait' } : {}),
          'data-testid': 'confirmation-modal-confirm',
        }, confirming ? '…' : confirmLabel)
      ),
      p.error && h('div', { style: STYLES.error, 'data-testid': 'confirmation-modal-error' }, p.error)
    )
  );
}
